import { generateAnnotationLabel } from 'features/kestrelViewer/hooks/useAnnotationCounters'
import { recordAnnotationCreated } from 'features/kestrelViewer/metrics'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { KestrelViewerMessageCallbackAPI } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { Vector2 } from 'three'
import {
  KestrelInspectionType,
  KVAnnotation,
  KVG1N6Annotation,
  KVNavyAnnotation,
  KVPointGeometry,
  RolodexEntryPatchJson,
  WorldPosition,
} from 'types'

import { KVMessageDef } from './index'

export function getNewPointAnnotationMessage() {
  return {
    type: 'newAnnotation',
    requiredData: ['annotationId'],

    callback: async (
      json: Record<string, unknown>,
      kv: KestrelViewerMessageCallbackAPI
    ) => {
      // Skip annotation creation when in temperature mode
      if (kv.selectedMode === 'temperature') {
        return
      }

      const annotation = KVAnnotation.fromJSON(json)
      annotation.userName = kv.user?.name ?? ''
      annotation.userEmail = kv.user?.email ?? ''
      annotation.label = generateAnnotationLabel(
        annotation.label,
        kv.nextAnnotationNumber
      )

      // Construct point geometry
      const pointGeometry = new KVPointGeometry(
        json.annotationPosition as WorldPosition,
        json.annotationNormal as WorldPosition
      )
      annotation.geometry = pointGeometry

      // Centered normal/position for zooming and photo selection
      annotation.centeredNormal = json.annotationNormal as WorldPosition
      annotation.centeredPosition = json.annotationPosition as WorldPosition

      const inspectionType = kv.kestrelModel?.data.inspection_type

      // Add Navy annotation if Navy inspection type
      if (inspectionType === KestrelInspectionType.NAVY) {
        annotation.navyAnnotation = new KVNavyAnnotation()
      }
      // Add G1N6 annotation if G1N6 inspection type
      if (inspectionType === KestrelInspectionType.G1N6) {
        annotation.g1n6Annotation = new KVG1N6Annotation()
      }

      if (json.inspectionArea) {
        annotation.tags = {
          ...annotation.tags,
          inspectionArea: {
            derivedTag: true,
            value: json.inspectionArea as string,
          },
        }
      }

      // Apply whiteboard tag immediately if in SystemView context
      if (kv.selectedWhiteboardId) {
        annotation.tags = {
          ...annotation.tags,
          whiteboard: {
            derivedTag: false,
            value: kv.selectedWhiteboardId,
          },
        }
      }

      // Update state with new annotation
      const updatedAnnotations = kv.annotations
        ? [...kv.annotations, annotation]
        : [annotation]
      kv.setAnnotations(updatedAnnotations)

      // Increment the annotation counter
      kv.setNextAnnotationNumber(kv.nextAnnotationNumber + 1)

      // Track annotation creation for session metrics
      recordAnnotationCreated()

      // Notify pixel stream about the label
      if (kv.pixelStream) {
        kv.pixelStream.emitUIInteraction({
          command: KestrelCommands.UPDATE_ANNOTATION,
          annotationId: annotation.id,
          annotationLabel: annotation.label,
        })
      }

      // Position popup in top right of screen
      const kvElementRect = kv.videoParent.current?.getBoundingClientRect()
      if (kvElementRect) {
        // Position in top right with some margin from edges
        const topRightX = kvElementRect.width - 475
        const topRightY = 20
        kv.setPopupMenuStartPosition(new Vector2(topRightX, topRightY))
      }
      kv.setSelectedMarkup(annotation)

      // Track annotation in rolodex using updated array to avoid race condition
      const patchData: RolodexEntryPatchJson = {
        id: kv.kestrelModel?.id ?? '',
        data: {
          annotations: updatedAnnotations.map((a) => KVAnnotation.toJSON(a)),
        },
      }
      kv.rolodexPatch([patchData])
    },
  } as KVMessageDef
}
